import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Typography from '@mui/material/Typography';
import { CREDENTIAL_PROVIDER_AUTOCOMPLETE_CLASS_NAME } from '../../../../theme/constants/autocomplete';
import AutocompleteList from '../../../autocomplete/AutocompleteList';
import AutocompletePaper from '../../../autocomplete/AutocompletePaper';
import useIsMobile from '../../../../hooks/useIsMobile';
import useGetInvoiceProviders from '../../../../hooks/useGetInvoiceProviders';
import ImageOption from '../../../options/ImageOption';
import ImageInput from '../../../inputs/ImageInput';
import ShoppingCartStepperNavigation from '../../ShoppingCartStepperNavigation';
import ShoppingCartSummaryActionButton from '../../ShoppingCartSummary/ShoppingCartSummaryActionButton';

const OTHER_PROVIDER = { id: 'other', name: 'Otro', integrated: false };

const CredentialProviderSelectorStep = ({
  prevStep,
  nextStep,
  selectedProvider,
  setSelectedProvider,
  currentStep,
}) => {
  const { providers, loading } = useGetInvoiceProviders();
  const isMobile = useIsMobile();
  const sortedProviders = useMemo(() => {
    const filteredProviders = [...providers]
      .filter((provider) => provider.iconUrl !== null)
      .sort((a, b) => {
        if (a.name === 'MiPyme') return -1;
        if (b.name === 'MiPyme') return 1;
        return 0;
      });
    return [...filteredProviders, OTHER_PROVIDER];
  }, [providers]);

  const handleProviderChange = useCallback(
    (_, newValue) => {
      setSelectedProvider(newValue);
    },
    [setSelectedProvider],
  );

  const renderInput = useCallback(
    (params) => (
      <ImageInput
        {...params}
        variant="outlined"
        placeholder="¿Dónde emites facturas?"
        src={selectedProvider?.iconUrl}
        IconComponent={FactCheckOutlinedIcon}
        alt={selectedProvider?.name}
        isMobile={isMobile}
      />
    ),
    [selectedProvider, isMobile],
  );

  const renderOption = useCallback((props, option) => {
    const { iconUrl: src, name } = option;
    return (
      <ImageOption
        src={src}
        alt={name}
        label={name}
        IconComponent={FactCheckOutlinedIcon}
        isMobile={isMobile}
        {...props}
      />
    );
  }, [isMobile]);
  return (
    <>
      <Autocomplete
        value={selectedProvider}
        onChange={handleProviderChange}
        options={sortedProviders}
        loading={loading}
        getOptionLabel={(option) => option.name}
        renderOption={renderOption}
        renderInput={renderInput}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        className={CREDENTIAL_PROVIDER_AUTOCOMPLETE_CLASS_NAME}
        ListboxComponent={AutocompleteList}
        PaperComponent={AutocompletePaper}
        disablePortal={isMobile}
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            height: '90px',
          },
          '& .MuiAutocomplete-listbox': {
            maxHeight: isMobile ? '200px' : '300px',
          },
        }}
      />
      <ShoppingCartStepperNavigation onBack={prevStep} currentStep={currentStep}>
        <ShoppingCartSummaryActionButton
          id="confirm-credential-provider-selector-button"
          label={(
            <Typography variant="small_3" color="white" fontWeight={600}>
              Continuar
            </Typography>
          )}
          disabled={!selectedProvider}
          onClick={nextStep}
          size="small"
        />
      </ShoppingCartStepperNavigation>
    </>
  );
};

CredentialProviderSelectorStep.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  setSelectedProvider: PropTypes.func.isRequired,
  selectedProvider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    iconUrl: PropTypes.string,
  }),
  currentStep: PropTypes.number.isRequired,
};

CredentialProviderSelectorStep.defaultProps = {
  selectedProvider: null,
};

export default CredentialProviderSelectorStep;
